import {
  KeyboardEvent,
  MouseEvent,
  ReactNode,
  Ref,
  forwardRef,
  useEffect,
  useRef,
  useState,
} from "react";
import { LayoutUtilProps } from "../../types";
import {
  FocusedItem,
  ItemType,
  ListViewWithItems,
  ListViewWithOutItems,
  Option,
} from "./internal/types";
import { ListViewOption } from "./ListViewOption";
import { ListViewOptionCell } from "./ListViewOptionCell";
import { useLayoutPropsUtil } from "../../internal/hooks";
import { useMergeRefs } from "../../hooks";
import { getFocusables, listViewKeyboardNavigation } from "./internal/utils";
import { mapOrder } from "../../internal/functions";
import { ListViewContext } from "./internal/ListViewContext";

import cx from "classnames";
import styles from "./ListView.module.scss";

/**
 * Props for the ListView component
 * @template T - The type of additional properties for items
 * @extends LayoutUtilProps
 */
export type ListViewProps<T> = LayoutUtilProps &
  (ListViewWithItems<T> | ListViewWithOutItems);

function ListViewInner<T>(props: ListViewProps<T>, ref: Ref<HTMLDivElement>) {
  const { layoutStyles, componentProps } = useLayoutPropsUtil(props);
  const {
    indeterminate,
    onSelectionChange,
    className,
    selected,
    defaultSelected,
    style,
    onKeyDown,
    ...remainingProps
  } = componentProps;

  const ListViewRef = useRef<HTMLDivElement>(null);
  const isFocusVisible = useRef(true);
  const combinedRef = useMergeRefs([ref, ListViewRef]);
  const [options, setOptions] = useState<Option[]>();
  const [currentActive, setCurrentActive] = useState<string>();
  const [selectedItems, setSelectedItems] = useState<unknown[]>(
    selected ?? defaultSelected ?? [],
  );
  const [focusedItem, setFocusedItem] = useState<FocusedItem>({
    row: 0,
    col: 0,
  });

  const styleCombined = {
    ...style,
    ...layoutStyles,
  };

  const onFocusHandler = () => {
    if (!isFocusVisible.current) return;
    if (!options) return;
    options[focusedItem.row].focusables[focusedItem.col].setAttribute(
      "data-interactive",
      "focus-visible",
    );
    return;
  };
  const onBlurHandler = () => {
    if (!options) return;
    options[focusedItem.row].focusables[focusedItem.col]?.removeAttribute(
      "data-interactive",
    );
    return;
  };

  const onKeyDownHandler = (e: KeyboardEvent<HTMLDivElement>) => {
    onKeyDown?.(e);
    if (!options) return;
    isFocusVisible.current = true;
    listViewKeyboardNavigation(
      e,
      options,
      focusedItem,
      setFocusedItem,
      ListViewRef.current,
      (id) => setCurrentActive(id),
    );
  };

  const onMouseDownHandler = (e: MouseEvent<HTMLDivElement>) => {
    isFocusVisible.current = false;
    if (options && ListViewRef.current) {
      options[focusedItem.row].focusables[focusedItem.col]?.removeAttribute(
        "data-interactive",
      );

      const closestFocusable = (e.target as HTMLElement).closest(
        "button:not(:disabled):not([aria-disabled='true']), input:not(:disabled):not([aria-disabled='true']), [data-anv='checkbox']",
      );

      const optionRow = (e.target as HTMLElement).closest(
        '[data-anv="listview-option"]',
      );
      if (!optionRow || optionRow.ariaDisabled) return;
      const row = options.findIndex((option) => option.node === optionRow);
      if (!closestFocusable) {
        (options[row].focusables[0] as HTMLElement).click();
        return;
      }
      const col = Array.prototype.indexOf.call(
        options[row].focusables,
        closestFocusable?.getAttribute("data-anv") === "checkbox"
          ? closestFocusable.children[0]
          : closestFocusable,
      );
      setFocusedItem({ row, col });
    }
  };

  useEffect(() => {
    if (!ListViewRef.current) return;
    const optionArr = ListViewRef.current.querySelectorAll<HTMLDivElement>(
      "div[data-anv='listview-option']:not([aria-disabled='true'])",
    );
    const makeTree = Array.from(optionArr).map((option) => {
      const childFocusables = getFocusables(option);
      return {
        node: option,
        focusables: childFocusables,
      };
    });
    setOptions(makeTree);
  }, [props.children]);

  const prevCallbackItems = useRef<unknown[]>([]);
  const prevControlledItems = useRef<unknown[]>(
    selected ?? defaultSelected ?? [],
  );

  useEffect(() => {
    if (!options) return;
    const compareArr = options.map((option) => option.node.ariaLabel);
    const orderedSelectedItems = mapOrder(selectedItems, compareArr, "label");
    if (
      JSON.stringify(prevCallbackItems.current) ===
      JSON.stringify(orderedSelectedItems)
    )
      return;

    onSelectionChange?.(orderedSelectedItems);
    prevCallbackItems.current = orderedSelectedItems;
  }, [onSelectionChange, options, selectedItems]);

  useEffect(() => {
    if (!selected) return;

    // Helper to extract label from either string or ItemType
    const getLabel = (item: unknown): string =>
      typeof item === "string" ? item : (item as ItemType<T>).label;

    const filterDuplicates = (selected as unknown[]).reduce<unknown[]>(
      (acc, current) => {
        const currentLabel = getLabel(current);
        const isDup = acc.find((item) => getLabel(item) === currentLabel);
        if (!isDup) {
          return [...acc, current];
        } else {
          return acc;
        }
      },
      [],
    );

    if (!options) return;
    const compareArr = options.map((option) => option.node.ariaLabel);
    const orderedSelected = mapOrder(filterDuplicates, compareArr, "label");
    if (
      JSON.stringify(prevControlledItems.current) ===
      JSON.stringify(orderedSelected)
    )
      return;
    setSelectedItems(orderedSelected);
    prevControlledItems.current = orderedSelected;
  }, [options, selected]);

  const ListViewClassNames = cx([styles["listview"]], className);

  if (remainingProps.items !== undefined) {
    const { children, items, ...rest } = remainingProps as Omit<
      ListViewWithItems<T>,
      keyof LayoutUtilProps
    >;
    const renderChildren = children as
      | (({ items }: { items: ItemType<T>[] }) => ReactNode)
      | undefined;
    return (
      <ListViewContext.Provider
        value={{
          options,
          onSelectionChange: onSelectionChange as
            | ((value: unknown[]) => void)
            | undefined,
          indeterminateItems: indeterminate ?? [],
          selectedItems,
          setSelectedItems,
          items,
          controlled: !!selected,
        }}
      >
        <div
          ref={combinedRef}
          className={ListViewClassNames}
          style={styleCombined}
          data-anv="listview"
          role="grid"
          aria-multiselectable="true"
          onFocus={onFocusHandler}
          onBlur={onBlurHandler}
          onKeyDown={onKeyDownHandler}
          onMouseDown={onMouseDownHandler}
          aria-activedescendant={currentActive}
          tabIndex={0}
          {...rest}
        >
          {renderChildren?.({
            items: items.map((item: ItemType<T>) => {
              return item;
            }),
          })}
        </div>
      </ListViewContext.Provider>
    );
  }

  const { children, ...rest } = remainingProps;
  return (
    <ListViewContext.Provider
      value={{
        options,
        onSelectionChange: onSelectionChange as
          | ((value: unknown[]) => void)
          | undefined,
        selectedItems,
        indeterminateItems: indeterminate ?? [],
        setSelectedItems,
        controlled: !!selected || !!indeterminate,
      }}
    >
      <div
        ref={combinedRef}
        className={ListViewClassNames}
        style={styleCombined}
        data-anv="listview"
        role="grid"
        onFocus={onFocusHandler}
        onBlur={onBlurHandler}
        onKeyDown={onKeyDownHandler}
        onMouseDown={onMouseDownHandler}
        aria-activedescendant={currentActive}
        tabIndex={0}
        {...rest}
      >
        {children}
      </div>
    </ListViewContext.Provider>
  );
}

ListViewInner.displayName = "ListView";

/**
 * ListView component for displaying a list of selectable items in a grid layout.
 *
 * Features:
 * - Supports single and multiple selection modes
 * - Keyboard navigation with arrow keys
 * - Customizable item rendering
 * - Controlled and uncontrolled modes
 * - Accessible with proper ARIA attributes
 * - Focus management for interactive elements
 * - Layout utilities for positioning and spacing
 * - Automatic tracking ID generation for analytics
 *
 * @example
 * <ListView items={[{ label: "Item 1" }, { label: "Item 2" }]} onSelectionChange={handleSelection}>
 *   {({ items }) => items.map(item => (
 *     <ListView.Option key={item.label} item={item} />
 *   ))}
 * </ListView>
 */
export const ListView = Object.assign(
  forwardRef(ListViewInner) as <T>(
    props: ListViewProps<T> & { ref?: React.ForwardedRef<HTMLUListElement> },
  ) => ReturnType<typeof ListViewInner>,
  {
    /**
     * ListViewOption component for individual selectable items within a listview.
     *
     * Features:
     * - Displays selectable options with proper ARIA attributes
     * - Supports disabled state for non-selectable options
     * - Keyboard navigation support
     * - Accessible with screen reader support
     * - Automatic tracking ID generation for analytics
     *
     * @example
     * <ListView.Option item={{ label: "Option 1" }} />
     */
    Option: ListViewOption,
    /**
     * ListViewOptionCell component for rendering a cell within a listview option.
     *
     * Features:
     * - Renders a grid cell for option content
     * - Supports custom content and layout
     * - Accessible with proper ARIA attributes
     *
     * @example
     * <ListView.OptionCell>Custom cell content</ListView.OptionCell>
     */
    OptionCell: ListViewOptionCell,
  },
);
